import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { Rotulo, Selecao } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { api, get } from '../../nucleo/api';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { ErroGeral, useEnvio } from './formulario-local';

/** `nome` nulo para quem não lê o Financeiro (review r12 segurança B2, `nomes_ocultos`). */
type Ref = { id: string; nome: string | null; ativa: boolean; moeda?: string };
type Opcao = { id: string; nome: string; codigo: string | null };

interface ConfiguracaoFolha {
  categoria_id: string | null;
  conta_id: string | null;
  categoria: Ref | null;
  conta: Ref | null;
  categoria_padrao: Ref | null;
  conta_padrao: Ref | null;
  opcoes: { categorias: Opcao[]; contas: Opcao[] };
  /** Moeda da folha (r9 spec M1): as contas listadas são só desta moeda. */
  moeda?: string;
  /** Sem `financeiro.ver` (r9 segurança B1): listas vazias; a tela mostra só o que está configurado. */
  opcoes_ocultas?: boolean;
  /** Review r12 segurança (B2): sem ler o Financeiro, os nomes de categoria e conta vêm nulos. */
  nomes_ocultos?: boolean;
}

/**
 * Review r8 spec (B5): Folha > Configuração. Categoria de despesa e conta do Financeiro que recebem o custo da folha.
 * Vazio usa o padrão (categoria de código 4.1 e conta padrão do workspace). Limpar a configuração libera a categoria
 * no Financeiro quando nenhuma competência aberta a usa.
 */
export function Configuracao() {
  const { t } = useTranslation('folha');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const envio = useEnvio();
  const podeEditar = podeNoWorkspace(s, 'folha.fechar');
  const q = useQuery({ queryKey: ['folha', 'configuracao'], queryFn: () => get<ConfiguracaoFolha>('/folha/configuracao') });
  const [escolha, setEscolha] = useState<{ categoria?: string; conta?: string }>({});

  if (q.isPending) return <Carregando />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  const d = q.data;
  const categoria = escolha.categoria ?? d.categoria_id ?? '';
  const conta = escolha.conta ?? d.conta_id ?? '';
  const sujo = categoria !== (d.categoria_id ?? '') || conta !== (d.conta_id ?? '');

  // A configurada que ficou inativa (ou sumiu) continua na lista, marcada, para a tela mostrar o que está gravado.
  // Sem acesso ao Financeiro, a lista vem vazia de propósito: o configurado aparece pelo nome, sem marca de inativa.
  const ocultas = !!d.opcoes_ocultas;
  // Nome nulo (sem acesso ao Financeiro, review r12): a tela diz que não há acesso, em vez do nome.
  const nomeDe = (r: Ref) => r.nome ?? t('configuracao.nomeOculto');
  const comAtual = (lista: Opcao[], id: string | null, atual: Ref | null) => {
    const opcoes = lista.map((o) => ({ valor: o.id, rotulo: o.codigo ? `${o.codigo} ${o.nome}` : o.nome }));
    if (id && !lista.some((o) => o.id === id)) {
      const rotulo = !atual
        ? t('configuracao.removida')
        : ocultas
          ? nomeDe(atual)
          : !atual.ativa
            ? t('configuracao.inativa', { nome: nomeDe(atual) })
            : atual.moeda && d.moeda && atual.moeda !== d.moeda
              ? t('configuracao.outraMoeda', { nome: nomeDe(atual) })
              : nomeDe(atual);
      opcoes.unshift({ valor: id, rotulo });
    }
    return opcoes;
  };
  const editavel = podeEditar && !ocultas;
  const padrao = (r: Ref | null) => (r ? t('configuracao.padrao', { nome: nomeDe(r) }) : t('configuracao.semPadrao'));

  async function salvar() {
    const ok = await envio.enviar(() => api('PUT', '/folha/configuracao', { categoria_id: categoria || null, conta_id: conta || null }));
    if (ok) {
      setEscolha({});
      await qc.invalidateQueries({ queryKey: ['folha', 'configuracao'] });
      toast.sucesso(tn('comum.salvo'));
    }
  }

  return (
    <Cartao titulo={t('configuracao.titulo')}>
      <div className="flex flex-col gap-5">
        <p className="text-sm text-tinta-3">{t('configuracao.texto')}</p>
        <ErroGeral texto={envio.geral} />
        {ocultas && (
          <p role="note" data-folha-sem-acesso className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">
            {t('configuracao.semAcesso')}
          </p>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Rotulo id="folha-config-categoria" rotulo={t('configuracao.categoria')} erro={envio.erros.categoria_id} ajuda={t('configuracao.categoriaAjuda')}>
            <Selecao
              id="folha-config-categoria"
              disabled={!editavel}
              vazio={padrao(d.categoria_padrao)}
              opcoes={comAtual(d.opcoes.categorias, d.categoria_id, d.categoria)}
              value={categoria}
              onChange={(e) => setEscolha((a) => ({ ...a, categoria: e.target.value }))}
            />
          </Rotulo>
          <Rotulo id="folha-config-conta" rotulo={t('configuracao.conta')} erro={envio.erros.conta_id} ajuda={d.moeda && !ocultas ? `${t('configuracao.contaAjuda')} ${t('configuracao.contaMoeda', { moeda: d.moeda })}` : t('configuracao.contaAjuda')}>
            <Selecao
              id="folha-config-conta"
              disabled={!editavel}
              vazio={padrao(d.conta_padrao)}
              opcoes={comAtual(d.opcoes.contas, d.conta_id, d.conta)}
              value={conta}
              onChange={(e) => setEscolha((a) => ({ ...a, conta: e.target.value }))}
            />
          </Rotulo>
        </div>
        {editavel && (
          <div className="flex flex-wrap justify-end gap-2">
            <Botao variante="fantasma" disabled={!sujo} onClick={() => setEscolha({})}>
              {t('configuracao.descartar')}
            </Botao>
            <AcaoPrimaria>
              <Botao variante="primario" disabled={!sujo} carregando={envio.enviando} onClick={() => void salvar()}>
                {tn('comum.salvar')}
              </Botao>
            </AcaoPrimaria>
          </div>
        )}
      </div>
    </Cartao>
  );
}
